import { DownOutlined } from "@ant-design/icons"
import { Button, Checkbox } from "antd"
import React, {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

interface FilterOption<T> {
  label: string
  value: T
}

interface FilterDropdownButtonProps {
  icon: ReactNode
  label: string
  disabled?: boolean
  /** Small badge next to the label once > 0; the label/icon never change. */
  activeCount?: number
  /** One shared key per header (not a boolean per pill) makes only-one-open structural. */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** `close` is for single-select panels; a multi-select panel ignores it and stays open. */
  children: (close: () => void) => ReactNode
}

// Portals to document.body (the table below this header paints over an in-flow popup
// regardless of z-index — see docs/kb/decisions/2026-08-12-finance-row-popups-portal-to-body.md).
const POPUP_Z_INDEX = 1050
const PANEL_WIDTH_PX = 320 // matches Tailwind's max-w-xs
const VIEWPORT_MARGIN_PX = 8

// Keeps a fixed-position panel from rendering past the right edge of the viewport.
export function clampPanelLeft(
  triggerLeft: number,
  viewportWidth: number,
  panelWidth: number = PANEL_WIDTH_PX,
  margin: number = VIEWPORT_MARGIN_PX
): number {
  const maxLeft = viewportWidth - panelWidth - margin
  return Math.max(margin, Math.min(triggerLeft, maxLeft))
}

// On a viewport narrower than the panel's own max-width, the assumed width has to shrink too, or
// clampPanelLeft still leaves room for the (CSS-sized) panel to spill past the right edge.
export function clampPanelWidth(
  viewportWidth: number,
  panelWidth: number = PANEL_WIDTH_PX,
  margin: number = VIEWPORT_MARGIN_PX
): number {
  return Math.min(panelWidth, Math.max(0, viewportWidth - margin * 2))
}

// Finds the real scrolling ancestor: this app's shell pins to the viewport and scrolls
// #main-content instead, so a listener on `window` alone would never fire.
function getScrollableAncestor(element: HTMLElement | null): HTMLElement | Window {
  let node = element?.parentElement ?? null
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === "auto" || overflowY === "scroll") return node
    node = node.parentElement
  }
  return window
}

export function FilterDropdownButton({
  icon,
  label,
  disabled = false,
  activeCount = 0,
  open,
  onOpenChange,
  children
}: FilterDropdownButtonProps) {
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(
    null
  )
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Recomputes on `open` (not only on click) so a remount that comes back already-open
  // (e.g. the fetchingOptions Skeleton swap) doesn't stick at (0, 0) forever.
  useLayoutEffect(() => {
    if (!open) return
    const box = buttonRef.current?.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const effectivePanelWidth = clampPanelWidth(viewportWidth)
    setAnchor(
      box
        ? {
            top: box.bottom + 4,
            left: clampPanelLeft(box.left, viewportWidth, effectivePanelWidth)
          }
        : null
    )
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = () => onOpenChange(false)
    // Bubble phase, not capture: capture also sees a nested option list's own scroll.
    const scrollTarget = getScrollableAncestor(buttonRef.current)
    scrollTarget.addEventListener("scroll", close)
    window.addEventListener("resize", close)

    // mousedown fires before another trigger's click, so switching pills is one click.
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      onOpenChange(false)
    }
    document.addEventListener("mousedown", handlePointerDown)

    // Keyboard equivalent: Tab + Enter/Space on a non-pill control fires no mousedown.
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      onOpenChange(false)
    }
    document.addEventListener("focusin", handleFocusIn)

    return () => {
      scrollTarget.removeEventListener("scroll", close)
      window.removeEventListener("resize", close)
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("focusin", handleFocusIn)
    }
  }, [open, onOpenChange])

  const handleToggle = () => onOpenChange(!open)

  return (
    <>
      <Button
        ref={buttonRef}
        type="default"
        disabled={disabled}
        onClick={handleToggle}
        className="flex h-10 items-center gap-2 px-3"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-bg text-primary">
          {icon}
        </span>
        <span>{label}</span>
        {activeCount > 0 && (
          <span className="rounded-full bg-primary-bg px-1.5 py-0 text-xs font-semibold text-primary">
            {activeCount}
          </span>
        )}
        <DownOutlined className="text-[10px] text-gray-400" />
      </Button>

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed min-w-[220px] max-w-xs rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
            style={{
              zIndex: POPUP_Z_INDEX,
              top: anchor?.top ?? 0,
              left: anchor?.left ?? 0
            }}
          >
            {children(() => onOpenChange(false))}
          </div>,
          document.body
        )}
    </>
  )
}

export function MultiSelectFilterPanel<T extends string | number>({
  options,
  value,
  onChange
}: {
  options: FilterOption<T>[]
  value: T[]
  onChange: (value: T[]) => void
}) {
  const { t } = useTranslation()

  const toggle = (optionValue: T) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    )
  }

  if (!options.length) {
    return (
      <div className="px-2 py-1.5 text-sm text-gray-400">
        {t("finance.filters.noOptions")}
      </div>
    )
  }

  return (
    <div className="flex max-h-64 flex-col overflow-y-auto">
      {options.map((option) => (
        <Checkbox
          key={String(option.value)}
          checked={value.includes(option.value)}
          onChange={() => toggle(option.value)}
          className="rounded-md px-2 py-1.5 hover:bg-primary-bg"
        >
          {option.label}
        </Checkbox>
      ))}
    </div>
  )
}
